import React, { useState, useContext, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pathname } from 'Routes';
import { Button } from 'Components';
import { AppContext } from 'Context';
import { axios, baseUrl } from 'Api';
import logo from 'Assets/Login Logo.png';
import { InviteUsersIcon } from './InviteUsersIcon';
import styles from './index.module.css';

const transitionTimeout = 300;

export const InviteEachUser = () => {
  const navigate = useNavigate();
  const { setAppSnackbar } = useContext(AppContext);
  const getToken = () => localStorage.getItem('token');
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [linkCopied, setLinkCopied] = useState(false);
  const [publicId, setPublicId] = useState('');

  // Fetch the public ID used to build the shareable invite link.
  useEffect(() => {
    const fetchPublicId = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/getPublicId`,
          { token: getToken() },
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (response.data?.data?.id) {
          setPublicId(response.data.data.id);
        }
      } catch (error) {
        console.error('Failed to fetch public ID:', error);
      }
    };
    fetchPublicId();
  }, []);

  // Shareable invite link built from the backend-provided public ID.
  const inviteLink = useMemo(() => {
    const { origin } = window.location;
    const inviteId = publicId || localStorage.getItem('code') || 'a1b2c3d4';
    return `${origin}/viewer/invite/${inviteId}`;
  }, [publicId]);

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        // Fallback for non-secure contexts / older browsers
        const textArea = document.createElement('textarea');
        textArea.value = inviteLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setLinkCopied(true);
      setAppSnackbar({
        isVisible: true,
        message: 'Invite link copied to clipboard!',
      });
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (error) {
      console.error('Copy link failed:', error);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Could not copy the link. Please copy it manually.',
      });
    }
  };
  const isValidEmail = (email) => {
    // Basic check: has '@' and '.' after '@'
    const atIndex = email.indexOf('@');
    const dotIndex = email.lastIndexOf('.');
    return atIndex > 0 && dotIndex > atIndex;
  };

  const searchUserToInvite = async (email) => {
    try {
      const response = await axios.post(
        `${baseUrl}/searchUserToInvite`,
        { email },
        {
          headers: {
            Authorization: `Bearer ${getToken}`,
          },
        }
      );
      if (response.data?.data?.length) {
        setSuggestions(response?.data?.data);
      } else {
        setSuggestions([{ email: inputValue }]);
      }
    } catch (error) {
      console.error('Search user error:', error);
      setSuggestions([]);
    }
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setInputValue(value);
    setSelectedIndex(-1);

    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      if (isValidEmail(value)) {
        searchUserToInvite(value);
      } else {
        setSuggestions([]);
      }
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleSelectSuggestion = (user) => {
    const safeUser = {
      email: user.email,
      firstname: user.firstname || user.firstName || '',
      lastname: user.lastname || user.lastName || '',
      id: user.id || user.email,
      image: user.image || null,
      status: user.status || null,
    };

    // Prevent duplicates
    if (!selectedUsers.some((u) => u.email === safeUser.email)) {
      setSelectedUsers((prev) => [...prev, safeUser]);
    }

    setInputValue('');
    setSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (
          prev < suggestions.length - 1 ? prev + 1 : 0
        ));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (
          prev > 0 ? prev - 1 : suggestions.length - 1
        ));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        } else if (suggestions.length > 0) {
          // Select first item if no specific item is selected
          handleSelectSuggestion(suggestions[0]);
        }
        break;
      case 'Escape':
        setSuggestions([]);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleDelete = (emailToRemove) => {
    setSelectedUsers((prev) => prev.filter((user) => user.email !== emailToRemove));
  };
  const handleAddViewer = async () => {
    setLoading(true);
    try {
      const token = getToken(); // Get your token

      const response = await axios.post(
        `${baseUrl}/addViewer`,
        {
          data: selectedUsers, // 👈 Payload
          token, // 👈 Token in payload, not headers
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/plain, */*',
          },
        }
      );

      console.log('Viewer added:', response.data);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: response.data.message,
      });
      setSelectedUsers([]);
      navigate(Pathname.access);
    } catch (error) {
      console.error('Failed to add viewer:', error);
      // Optional: show error toast
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authPage}>
      {/* Background accents */}
      <div className={styles.bgOverlay}>
        <div className={styles.bgAccent1} />
        <div className={styles.bgAccent2} />
        <div className={styles.bgCircle} />
      </div>

      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.header}>
          <Link to={Pathname.home}>
            <img src={logo} alt="Projector Logo" className={styles.logo} />
          </Link>
        </div>

        {/* Main Content — two-column (fits without scrolling) */}
        <div className={styles.mainContent}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Invite Friends &amp; Family</h1>
            <p className={styles.subtitle}>
              Create your ultimate viewing experience and share precious memories like never before!
            </p>
          </div>

          <div className={styles.inviteSection}>
            {/* Left: illustration panel */}
            <div className={styles.imageContainer}>
              <div className={styles.imageGlow} />
              <InviteUsersIcon />
            </div>

            {/* Right: form */}
            <div className={styles.formContainer}>
              {/* Share Link Section */}
              <div className={styles.linkSection}>
                <div className={styles.linkSectionHeader}>
                  <span className={styles.linkSectionTitle}>Share your invite link</span>
                  <span className={styles.linkSectionHint}>
                    Copy this link and share it with anyone you&apos;d like to invite
                  </span>
                </div>
                <div className={styles.linkBox}>
                  <span className={styles.linkGlyph} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className={styles.linkUrl} title={inviteLink}>{inviteLink}</span>
                  <button
                    type="button"
                    className={`${styles.copyButton} ${linkCopied ? styles.copyButtonCopied : ''}`}
                    onClick={handleCopyLink}
                    aria-label="Copy invite link"
                  >
                    {linkCopied ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Copy Link</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className={styles.dividerRow}>
                <span className={styles.dividerLine} />
                <span className={styles.dividerText}>or invite by email</span>
                <span className={styles.dividerLine} />
              </div>

              {/* Email Input Section */}
              <div className={styles.inputSection}>
                <label className={styles.inputLabel}>Invite by Email</label>
                <div className={styles.inputWrapper}>
                  <div className={styles.inputContainer}>
                    <input
                      type="email"
                      value={inputValue}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Enter email address to invite"
                      className={styles.emailInput}
                      autoComplete="email"
                    />
                    {inputValue && isValidEmail(inputValue) && (
                      <div className={styles.validIcon}>✓</div>
                    )}
                  </div>

                  {/* Suggestions Dropdown */}
                  {suggestions?.length > 0 && (
                    <div className={styles.suggestionsDropdown}>
                      {suggestions.map((user, index) => (
                        <button
                          key={user.id || index}
                          className={`${styles.suggestionItem} ${
                            index === selectedIndex ? styles.suggestionItemSelected : ''
                          }`}
                          onClick={() => handleSelectSuggestion(user)}
                          onMouseEnter={() => setSelectedIndex(index)}
                          type="button"
                        >
                          <div className={styles.userAvatar}>
                            {user.image ? (
                              <img src={user.image} alt={user.firstname || user.email} />
                            ) : (
                              <span className={styles.avatarLetter}>
                                {(user.firstname?.[0] || user.email?.[0])?.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className={styles.userDetails}>
                            <div className={styles.userName}>
                              {user.firstname && user.lastname
                                ? `${user.firstname} ${user.lastname}`
                                : user.email}
                            </div>
                            <div className={styles.userEmail}>{user.email}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {inputValue && !isValidEmail(inputValue) && (
                  <p className={styles.errorText}>Please enter a valid email address</p>
                )}
              </div>

              {/* Selected Users */}
              {selectedUsers.length > 0 && (
                <div className={styles.selectedUsersSection}>
                  <p className={styles.sectionLabel}>
                    Selected Users (
                    {selectedUsers.length}
                    )
                  </p>
                  <div className={styles.selectedUsersScrollContainer}>
                    <div className={styles.selectedUsersContainer}>
                      {selectedUsers.map((user) => (
                      <div key={user.email} className={styles.selectedUserChip}>
                        <div className={styles.chipAvatar}>
                          {user.image ? (
                            <img src={user.image} alt={user.firstname || user.email} />
                          ) : (
                            <span className={styles.chipAvatarLetter}>
                              {(user.firstname?.[0] || user.email?.[0])?.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className={styles.chipInfo}>
                          <span className={styles.chipName}>
                            {user.firstname && user.lastname
                              ? `${user.firstname} ${user.lastname}`
                              : user.email}
                          </span>
                          <span className={styles.chipEmail}>{user.email}</span>
                        </div>
                        <button
                          className={styles.removeChip}
                          onClick={() => handleDelete(user.email)}
                          type="button"
                          aria-label="Remove user"
                        >
                          ×
                        </button>
                      </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <Button
                  onClick={handleAddViewer}
                  isDisabled={loading || selectedUsers?.length === 0}
                  blockText
                  isBlue
                  isFullWidth
                  isLarge
                >
                  {loading ? (
                    <div className={styles.loadingContent}>
                      <div className={styles.spinner} />
                      <span>Sending Invites...</span>
                    </div>
                  ) : (
                    <div className={styles.buttonContent}>
                      <span style={{ fontSize: '20px' }}>
                        Send
                        {' '}
                        {selectedUsers.length}
                        {' '}
                        Invitation
                        {selectedUsers.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </Button>

                <button
                  className={styles.skipButton}
                  onClick={() => navigate(Pathname.access)}
                  type="button"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
